import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { AuctionGrid } from "@/components/auction-grid";
import { CardGridSkeleton, ErrorState } from "@/components/data-state";
import { RouteErrorBoundary, RouteNotFoundBoundary } from "@/components/route-boundaries";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { fetchAllActive } from "@/lib/auctions";
import {
  findLanding,
  relatedLandings,
  matchesLanding,
  type LandingDefinition,
  type LandingFaq,
} from "@/lib/seo-landing";
import { absoluteUrl, socialImageMeta, breadcrumbLd } from "@/lib/seo";
import { LOCALES, LOCALE_PREFIX, SITE_LOCALE_HREFLANG } from "@/i18n/config";
import { useTranslation } from "@/i18n";

export const Route = createFileRoute("/collections/$slug")({
  loader: ({ params }) => {
    const landing = findLanding(params.slug);
    if (!landing) throw notFound();
    return { landing };
  },
  head: ({ loaderData }) => {
    const l = loaderData?.landing;
    if (!l) {
      return {
        meta: [
          { title: "Collections — BidNova Auctions" },
          { name: "robots", content: "noindex,follow" },
        ],
      };
    }
    const path = `/collections/${l.slug}`;
    const url = absoluteUrl(path);
    return {
      meta: [
        { title: l.h1 },
        { name: "description", content: l.h1 },
        { name: "robots", content: "index,follow" },
        { property: "og:title", content: l.h1 },
        { property: "og:description", content: l.h1 },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        ...socialImageMeta(),
      ],
      links: [
        { rel: "canonical", href: url },
        ...LOCALES.map((loc) => ({
          rel: "alternate",
          hrefLang: SITE_LOCALE_HREFLANG[loc],
          href: `https://bidnovaauctions.uk${LOCALE_PREFIX[loc]}${path}`,
        })),
        { rel: "alternate", hrefLang: "x-default", href: `https://bidnovaauctions.uk${path}` },
      ],
      scripts: [
        breadcrumbLd([
          { name: "Home", path: "/" },
          { name: "Auctions", path: "/auctions" },
          { name: "Collections", path: "/collections" },
          { name: l.label, path },
        ]),
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: l.h1,
            url,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: l.faqs.map((f: { qDefault: string; aDefault: string }) => ({
              "@type": "Question",
              name: f.qDefault,
              acceptedAnswer: { "@type": "Answer", text: f.aDefault },
            })),
          }),
        },
      ],
    };
  },
  component: CollectionLandingPage,
  errorComponent: RouteErrorBoundary,
  notFoundComponent: RouteNotFoundBoundary,
});

function CollectionLandingPage() {
  const { landing } = Route.useLoaderData();
  const { t } = useTranslation("landing");
  const intro = t(`${landing.i18nKey}.intro`, landing.h1);

  const { data, isLoading, isError, refetch, isRefetching } = useQuery({
    queryKey: ["all-active"],
    queryFn: fetchAllActive,
    refetchInterval: 60000,
  });
  const matched = (data ?? []).filter((a) => matchesLanding(a, landing));
  const related = relatedLandings(landing);

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-12">
      <nav aria-label="Breadcrumb" className="mb-6 text-xs text-muted-foreground">
        <ol className="flex flex-wrap items-center gap-1">
          <li><Link to="/" className="hover:text-foreground">{t("common.breadcrumbHome", "Home")}</Link></li>
          <li aria-hidden="true">/</li>
          <li><Link to="/auctions" className="hover:text-foreground">{t("common.breadcrumbAuctions", "Auctions")}</Link></li>
          <li aria-hidden="true">/</li>
          <li><Link to="/collections" className="hover:text-foreground">{t("common.breadcrumbCollections", "Collections")}</Link></li>
          <li aria-hidden="true">/</li>
          <li aria-current="page" className="text-foreground">{landing.label}</li>
        </ol>
      </nav>

      <header className="mb-8">
        <p className="text-xs uppercase tracking-widest text-gold">{t("common.breadcrumbCollections", "Collections")}</p>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl">{landing.h1}</h1>
        <p className="mt-3 max-w-3xl text-muted-foreground">{intro}</p>
      </header>

      <section aria-labelledby="listings" className="mt-4">
        <h2 id="listings" className="sr-only">Live listings</h2>
        {isLoading ? (
          <CardGridSkeleton count={8} />
        ) : isError ? (
          <ErrorState
            description="We couldn't load these listings."
            onRetry={() => refetch()}
            retrying={isRefetching}
          />
        ) : matched.length > 0 ? (
          <AuctionGrid auctions={matched} />
        ) : (
          <div className="rounded-2xl border border-border/60 bg-card p-10 text-center">
            <p className="text-sm text-muted-foreground">
              {t("common.emptyState", "No {label} auctions are live right now. New listings go up throughout the day — browse all auctions or check back shortly.", { label: landing.label })}
            </p>
            <Link
              to="/auctions"
              search={landing.browseSearch as never}
              className="mt-4 inline-block text-gold hover:underline"
            >
              {t("common.browseAllCta", "See all matching listings")}
            </Link>
          </div>
        )}
        {matched.length > 0 && (
          <div className="mt-6 text-center">
            <Link
              to="/auctions"
              search={landing.browseSearch as never}
              className="inline-block rounded-full border border-gold/30 bg-gold/5 px-4 py-2 text-sm hover:bg-gold/10"
            >
              {t("common.browseAllCta", "See all matching listings")}
            </Link>
          </div>
        )}
      </section>

      {related.length > 0 && (
        <section aria-labelledby="related-collections" className="mt-14">
          <h2 id="related-collections" className="font-display text-2xl">{t("common.relatedLabel", "Related collections")}</h2>
          <nav aria-label="Related collections" className="mt-4 flex flex-wrap gap-2 text-sm">
            {related.map((r: LandingDefinition) => (
              <Link
                key={r.slug}
                to="/collections/$slug"
                params={{ slug: r.slug }}
                className="rounded-full border border-gold/30 bg-gold/5 px-3 py-1 hover:bg-gold/10"
              >
                {r.label}
              </Link>
            ))}
            <Link to="/collections" className="rounded-full border border-border px-3 py-1 hover:bg-muted">
              {t("common.breadcrumbCollections", "Collections")}
            </Link>
          </nav>
        </section>
      )}

      <section aria-labelledby="faq" className="mt-14">
        <h2 id="faq" className="font-display text-2xl">{t("common.faqLabel", "Frequently asked questions")}</h2>
        <Accordion type="single" collapsible className="mt-4">
          {landing.faqs.map((f: LandingFaq) => (
            <AccordionItem key={f.qKey} value={f.qKey}>
              <AccordionTrigger>{t(`${f.qKey.split(".")[0]}.${f.qKey.split(".")[1]}`, f.qDefault)}</AccordionTrigger>
              <AccordionContent>{t(`${f.aKey.split(".")[0]}.${f.aKey.split(".")[1]}`, f.aDefault)}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <section className="mt-14 grid gap-8 md:grid-cols-2">
        <div>
          <h2 className="font-display text-2xl">{t("common.whyBuyTitle", "Why buy on BidNova")}</h2>
          <p className="mt-3 text-sm text-muted-foreground">{t("common.whyBuyBody", "BidNova is a UK-registered marketplace. Every listing is sold by a verified seller with a public rating, priced in GBP, and paid via Stripe. Funds are held until you confirm your order arrives as described.")}</p>
          <ul className="mt-4 space-y-2 text-sm text-muted-foreground list-disc pl-5">
            <li>{t("common.whyBuyPoint1", "Stripe-secured payments with buyer protection.")}</li>
            <li>{t("common.whyBuyPoint2", "Verified UK sellers with public ratings and dispute resolution.")}</li>
            <li>{t("common.whyBuyPoint3", "Prices in GBP — no surprise conversion fees.")}</li>
          </ul>
        </div>
        <div>
          <h2 className="font-display text-2xl">{t("common.sellingTitle", "Selling {label}?", { label: landing.label })}</h2>
          <p className="mt-3 text-sm text-muted-foreground">{t("common.sellingBody", "Approved sellers can list cards alongside singles and sealed product from one dashboard. Add photos, grading details and a starting price to get listed in minutes.")}</p>
          <div className="mt-4">
            <Link to="/sell" className="inline-block rounded-full border border-gold px-4 py-2 text-sm hover:bg-gold/10">
              {t("common.sellCta", "Start selling")}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
